import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ContractCancel,
  OnContractExpiry,
  PlanCategory,
} from '@fitness-force/enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';

import type { CancelSubscriptionRequest, CancellationReason } from '@fitness-force/models';
import { ClientSubscriptionService, ToastMessageService } from '@fitness-force/api';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { EMPTY, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'subscription-cancel',
  templateUrl: './subscription-cancel.component.html',
  styleUrl: './subscription-cancel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastMessageService],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    RadioButtonModule,
    MessageModule,
    TranslateModule,
    DatePipe,
    NgClass,
    DatePickerModule,
    SelectModule,
    TextareaModule,
  ],
})
export class SubscriptionCancelComponent implements OnInit, OnDestroy {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly toastMessageService = inject(ToastMessageService);
  translate = inject(TranslateService);
  private readonly clientSubscriptionService = inject(ClientSubscriptionService);

  readonly visible = input(false);
  readonly subscriptionDetails = input<any>(undefined, { alias: 'subscription' });
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly closeEvent = output<{
    reloadData: boolean;
    subscription: any;
  }>({ alias: 'closeEvent' });

  CancelSubscriptionForm: UntypedFormGroup = new UntypedFormGroup({});
  isFormSubmitting = false;
  CancelType: ContractCancel;
  cancellationDate: Date;
  text = 'TODAY';
  selectedCancelType = 'ImmediateCancel';
  cancellationReasons: CancellationReason[] = [];
  showCustomDatePicker = false;
  showImmediateRecentDatePicker = false;
  minDate: Date = new Date();

  earliestCancellationDate: string | null = null;
  isPreviewLoading = false;
  isValidatingDate = false;
  isImmediateRecentDateValid = false;
  immediateRecentValidationMessage = '';

  private readonly destroy$ = new Subject<void>();
  private readonly earliestPreviewTrigger$ = new Subject<void>();
  private readonly immediateRecentValidate$ = new Subject<Date>();

  ngOnInit(): void {
    this.cancellationDate = new Date();
    this.CancelSubscriptionForm = this.fb.group({
      cancel_type: ['ImmediateCancel', Validators.required],
      reason: [''],
      custom_date: [null],
      cancellation_reason_master: [null, Validators.required],
    });
    this.setupEarliestPreviewStream();
    this.setupImmediateRecentValidateStream();
    this.loadCancellationReasons();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupEarliestPreviewStream(): void {
    this.earliestPreviewTrigger$
      .pipe(
        switchMap(() => {
          this.isPreviewLoading = true;
          this.earliestCancellationDate = null;
          this.cd.detectChanges();

          return this.clientSubscriptionService
            .GetEarliestCancellationDatePreview(
              this.tenantId(),
              this.clientId(),
              this.subscriptionDetails().id,
            )
            .pipe(
              finalize(() => {
                this.isPreviewLoading = false;
                this.cd.detectChanges();
              }),
              catchError((error) => {
                this.handleError(error);
                return EMPTY;
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.earliestCancellationDate = response.earliest_cancellation_date;
        this.cancellationDate = new Date(response.earliest_cancellation_date);
        this.text = 'EARLIEST';
        this.cd.detectChanges();
      });
  }

  private setupImmediateRecentValidateStream(): void {
    this.immediateRecentValidate$
      .pipe(
        switchMap((selectedDate) => {
          this.isValidatingDate = true;
          this.isImmediateRecentDateValid = false;
          this.immediateRecentValidationMessage = '';
          this.cd.detectChanges();

          return this.clientSubscriptionService
            .ValidateImmediateRecentCancellationDate(
              this.tenantId(),
              this.clientId(),
              this.subscriptionDetails().id,
              {
                subscription_cancel: ContractCancel.ImmediateRecent,
                cancellation_date: this.formatDateOnly(selectedDate),
              },
            )
            .pipe(
              finalize(() => {
                this.isValidatingDate = false;
                this.cd.detectChanges();
              }),
              catchError((error) => {
                this.handleError(error);
                return EMPTY;
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.isImmediateRecentDateValid = response.is_valid;
        this.immediateRecentValidationMessage = response.message || '';
        this.cd.detectChanges();
      });
  }

  loadCancellationReasons(): void {
    this.clientSubscriptionService
      .GetCancellationReasons(this.tenantId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.cancellationReasons = response.items
            .filter((reason) => reason.is_active)
            .sort((a, b) => a.display_order - b.display_order);

          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Failed to load cancellation reasons:', error);
        },
      });
  }

  GetCancelType(cancelType: string) {
    if (this.selectedCancelType === cancelType && this.CancelTypeFormControl.value === cancelType) {
      return;
    }

    const cancelTypeEnum =
      ContractCancel[cancelType as keyof typeof ContractCancel];
    this.CancelTypeFormControl.setValue(cancelType);
    this.selectedCancelType = cancelType;
    this.resetOptionSpecificState();

    if (cancelType === 'Custom') {
      this.showCustomDatePicker = true;
      this.showImmediateRecentDatePicker = false;
      this.text = 'CUSTOM';
      const customDate = this.CancelSubscriptionForm.get('custom_date')?.value;
      if (customDate) {
        this.cancellationDate = new Date(customDate);
      } else {
        const today = new Date();
        this.cancellationDate = today;
        this.CancelSubscriptionForm.get('custom_date')?.setValue(today);
      }
    } else if (cancelType === 'ImmediateRecent') {
      this.showCustomDatePicker = false;
      this.showImmediateRecentDatePicker = true;
      this.text = 'IMMEDIATE_RECENT';
      const today = new Date();
      this.cancellationDate = today;
      this.CancelSubscriptionForm.get('custom_date')?.setValue(today);
      this.immediateRecentValidate$.next(today);
    } else if (cancelType === 'EarliestSubscriptionCancellationDate') {
      this.showCustomDatePicker = false;
      this.showImmediateRecentDatePicker = false;
      this.text = 'EARLIEST';
      this.CancelSubscriptionForm.get('custom_date')?.setValue(null);
      this.earliestPreviewTrigger$.next();
    } else {
      this.showCustomDatePicker = false;
      this.showImmediateRecentDatePicker = false;
      this.CancelSubscriptionForm.get('custom_date')?.setValue(null);

      if (
        cancelTypeEnum == ContractCancel.ImmediateCancel ||
        cancelTypeEnum.toString() ==
          ContractCancel[ContractCancel.ImmediateCancel]
      ) {
        this.cancellationDate = new Date();
        this.text = 'TODAY';
      } else if (
        cancelType == ContractCancel[ContractCancel.EndOfBillingPeriod]
      ) {
        this.text = 'END_OF_BILLING';
        const minusOneDay = new Date(
          this.subscriptionDetails().next_charge_date,
        );
        minusOneDay.setDate(
          new Date(this.subscriptionDetails().next_charge_date).getDate() - 1,
        );
        this.cancellationDate = minusOneDay;
      } else if (cancelType == ContractCancel[ContractCancel.EndOfContract]) {
        this.cancellationDate = this.subscriptionDetails().end_date;
        this.text = 'LAST_DAY';
      }
    }
    this.cd.detectChanges();
  }

  onCustomDateChange(event: Date | null): void {
    if (!event) {
      return;
    }

    this.cancellationDate = new Date(event);
    this.CancelSubscriptionForm.get('custom_date')?.setValue(new Date(event));

    if (this.selectedCancelType === 'ImmediateRecent') {
      this.isImmediateRecentDateValid = false;
      this.immediateRecentValidationMessage = '';
      this.immediateRecentValidate$.next(new Date(event));
    }

    this.cd.detectChanges();
  }

  private resetOptionSpecificState(): void {
    this.earliestCancellationDate = null;
    this.isPreviewLoading = false;
    this.isValidatingDate = false;
    this.isImmediateRecentDateValid = false;
    this.immediateRecentValidationMessage = '';
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get ShowEndOfBillingButton() {
    const category = this.subscriptionDetails().subscription_category;
    if (
      category == PlanCategory.PaidInFull ||
      category == PlanCategory[PlanCategory.PaidInFull]
    )
      return false;
    else return true;
  }

  get ShowEndOfContractButton() {
    const category = this.subscriptionDetails().subscription_category;
    const contractExpiry = this.subscriptionDetails().on_contract_expiry;
    if (
      category == PlanCategory.PaidInFull ||
      category == PlanCategory[PlanCategory.PaidInFull]
    ) {
      if (
        contractExpiry == OnContractExpiry.ContractAutoRenew ||
        contractExpiry ==
          OnContractExpiry[OnContractExpiry.ContractAutoRenew] ||
        contractExpiry == OnContractExpiry.ContractConvertsToPayAsYouGo ||
        contractExpiry ==
          OnContractExpiry[OnContractExpiry.ContractConvertsToPayAsYouGo]
      ) {
        return true;
      } else return false;
    } else if (
      category == PlanCategory.PayAsYouGo ||
      category == PlanCategory[PlanCategory.PayAsYouGo]
    )
      return false;
    else if (
      category == PlanCategory.Contract ||
      category == PlanCategory[PlanCategory.Contract]
    ) {
      if (
        contractExpiry == OnContractExpiry.ContractAutoRenew ||
        contractExpiry ==
          OnContractExpiry[OnContractExpiry.ContractAutoRenew] ||
        contractExpiry == OnContractExpiry.ContractConvertsToPayAsYouGo ||
        contractExpiry ==
          OnContractExpiry[OnContractExpiry.ContractConvertsToPayAsYouGo]
      ) {
        return true;
      } else return false;
    } else return false;
  }

  get showDatePicker(): boolean {
    return this.showCustomDatePicker || this.showImmediateRecentDatePicker;
  }

  get canSubmitCancellation(): boolean {
    if (
      !this.CancelSubscriptionForm.valid ||
      this.isFormSubmitting ||
      this.isPreviewLoading ||
      this.isValidatingDate
    ) {
      return false;
    }

    if (this.selectedCancelType === 'ImmediateRecent') {
      return this.isImmediateRecentDateValid && !!this.cancellationDate;
    }

    if (this.selectedCancelType === 'EarliestSubscriptionCancellationDate') {
      return !!this.earliestCancellationDate;
    }

    return true;
  }

  CancelSubscription() {
    if (!this.canSubmitCancellation) {
      Object.keys(this.CancelSubscriptionForm.controls).forEach((field) => {
        const control = this.CancelSubscriptionForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
      return;
    }

    const cancelSubscription: CancelSubscriptionRequest =
      {} as CancelSubscriptionRequest;
    cancelSubscription.cancellation_reason =
      this.CancelSubscriptionForm.value.reason;
    cancelSubscription.subscription_cancel = this.CancelTypeFormControl.value;

    const selectedReason =
      this.CancelSubscriptionForm.value.cancellation_reason_master;
    if (selectedReason) {
      cancelSubscription.cancellation_reason_master_id = selectedReason.id;
    }

    if (this.selectedCancelType === 'Custom' && this.cancellationDate) {
      cancelSubscription.cancellation_date =
        this.cancellationDate.toISOString();
    } else if (
      this.selectedCancelType === 'ImmediateRecent' &&
      this.cancellationDate
    ) {
      cancelSubscription.cancellation_date =
        this.cancellationDate.toISOString();
    } else if (
      this.selectedCancelType === 'EarliestSubscriptionCancellationDate' &&
      this.earliestCancellationDate
    ) {
      cancelSubscription.cancellation_date = this.earliestCancellationDate;
    }

    this.isFormSubmitting = true;
    this.clientSubscriptionService
      .CancelSubscription(
        this.tenantId(),
        this.clientId(),
        this.subscriptionDetails().id,
        cancelSubscription,
      )
      .pipe(
        finalize(() => {
          this.isFormSubmitting = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: async () => {
          this.translate
            .get('SUBSCRIPTION_CANCEL.MSG_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
          await this.timeout(1000);
          const subscriptionData = this.subscriptionDetails();
          if (subscriptionData) {
            subscriptionData.scheduled_cancellation_date =
              this.cancellationDate.toUTCString();
            subscriptionData.cancel_reason = this.CancelTypeFormControl.value;
          }
          this.closePopup(true);
        },
        error: (error: unknown) => {
          this.handleError(error);
        },
      });
  }

  handleError(error: unknown) {
    this.toastMessageService.showError(error);
  }

  closePopup(reloadData: boolean) {
    this.closeEvent.emit({
      reloadData: false,
      subscription: this.subscriptionDetails(),
    });
    this.CancelSubscriptionForm.reset();
    this.resetOptionSpecificState();
    this.showCustomDatePicker = false;
    this.showImmediateRecentDatePicker = false;
    this.selectedCancelType = 'ImmediateCancel';
    this.closeEvent.emit({
      reloadData: reloadData,
      subscription: this.subscriptionDetails(),
    });
  }

  timeout(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isMinimumDateTimeValue(eta: Date): boolean {
    return (
      new Date(eta).getTime() === new Date('0001-01-01T00:00:00').getTime()
    );
  }

  get CancelTypeFormControl(): UntypedFormControl {
    return this.CancelSubscriptionForm.controls
      .cancel_type as UntypedFormControl;
  }

  get CancellationReasonMasterFormControl(): UntypedFormControl {
    return this.CancelSubscriptionForm.controls
      .cancellation_reason_master as UntypedFormControl;
  }

  get isVisible(): boolean {
    return this.visible() && this.subscriptionDetails();
  }
}
